import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '../../../../shared/di/container.js';
import { logAuthEvent } from '../../../../core/debug/authDebugLog.js';

const AUTH_SESSION_QUERY_KEY = ['auth', 'session'];

export function useAuthSession() {
  return useQuery({
    queryKey: AUTH_SESSION_QUERY_KEY,
    queryFn: () => container.auth.getSessionUseCase(),
    staleTime: Infinity,
  });
}

export function useAuthSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    try {
      const unsubscribe = container.auth.observeAuthStateUseCase(async (session) => {
        logAuthEvent('useAuthSync:received', { hasSession: !!session, uid: session?.uid });
        queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);

        if (!session) return;

        try {
          const fullName = (session.displayName ?? '').trim();
          const [firstName = '', ...lastNameParts] = fullName.split(/\s+/).filter(Boolean);
          const lastName = lastNameParts.join(' ');

          await container.user.upsertUserUseCase({
            id: session.uid,
            email: session.email,
            firstName,
            lastName,
            phone: null,
            ignoreDuplicates: true,
          });
          logAuthEvent('useAuthSync:upsertSuccess', { uid: session.uid });
        } catch (err) {
          const message = (err as Error)?.message ?? String(err);
          logAuthEvent('useAuthSync:upsertFailed', { uid: session.uid, message });
          // Stale session (db:reset or user deletion) → force sign-out
          if (
            message.includes('profiles_id_fkey') ||
            message.includes('profiles_email_key')
          ) {
            logAuthEvent('useAuthSync:FORCED_LOGOUT', { uid: session.uid, reason: message });
            await container.auth.logoutUseCase().catch(() => {});
            queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, null);
            return;
          }
          console.warn('Auth sync to Supabase table failed:', err);
        }
      });

      return () => unsubscribe();
    } catch (err) {
      console.error('Failed to initialize AuthSync:', err);
    }
  }, [queryClient]);
}
